import {
  ColorHelper,
  DesignTokens,
  LabeledColorSwatch,
  Menu,
  MenuItem,
  Space,
  SubMenu,
  ValueOrFallback,
} from "melco-ui";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import isEmpty from "lodash-es/isEmpty";
import { useDesignElementList } from "../../hooks/designMappings/useDesignElementList";
import { useDesignElementTitle } from "../../hooks/designMappings/useDesignElementTitle";
import { ServiceType } from "../../helper/serviceType";

const { spacings } = DesignTokens;

const { rgbToARGB } = ColorHelper;

const DesignElementListItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

type DesignElementListProps = {
  designElementList: ReturnType<typeof useDesignElementList>;
  setCurrentDesignElementIndex: (designElementIndex: number) => void;
  serviceType: ServiceType;
};

export const DesignElementList: React.FC<DesignElementListProps> = ({
  designElementList,
  setCurrentDesignElementIndex,
  serviceType,
}) => {
  const { t } = useTranslation();

  const {
    letteringObjects,
    colorObjects,
    designObjects,
    mappableElements,
    groupIndex,
  } = designElementList;

  const elementTitle = useDesignElementTitle(serviceType);

  const isDesignColorsShown = !isEmpty(colorObjects);
  const isLayersShown = !isEmpty(designObjects);

  return (
    <Menu
      mode="inline"
      inlineIndent={spacings.xlarge}
      defaultOpenKeys={["design-colors", "design-layers"]}
    >
      {letteringObjects.map((element) => {
        const { id } = element;

        return (
          <MenuItem
            key={id}
            onClick={() =>
              setCurrentDesignElementIndex(
                mappableElements.findIndex((e) => e === element)
              )
            }
          >
            <DesignElementListItem>
              <Space size="small">
                <ValueOrFallback
                  value={elementTitle(element, groupIndex).title}
                  fallback={t("global.fallback.empty")}
                />
              </Space>
            </DesignElementListItem>
          </MenuItem>
        );
      })}

      {isDesignColorsShown && (
        <SubMenu
          key="design-colors"
          title={t("libraries.designs.detail.form.element_list.design_colors")}
        >
          {colorObjects.map((element) => {
            const { id, color } = element;

            return (
              <MenuItem
                key={id}
                onClick={() =>
                  setCurrentDesignElementIndex(
                    mappableElements.findIndex((e) => e === element)
                  )
                }
              >
                <DesignElementListItem>
                  <Space size="small">
                    {color?.rgb ? (
                      <LabeledColorSwatch
                        argb={rgbToARGB(color?.rgb ?? undefined)}
                        label={
                          <ValueOrFallback
                            value={elementTitle(element, groupIndex).title}
                            fallback={t("global.fallback.empty")}
                          />
                        }
                      />
                    ) : (
                      <ValueOrFallback
                        value={elementTitle(element, groupIndex).title}
                        fallback={t("global.fallback.empty")}
                      />
                    )}
                  </Space>
                </DesignElementListItem>
              </MenuItem>
            );
          })}
        </SubMenu>
      )}

      {isLayersShown && (
        <SubMenu
          key="design-layers"
          title={t("libraries.designs.detail.form.element_list.layers")}
        >
          {designObjects.map((element) => {
            const { id } = element;

            return (
              <MenuItem
                key={id}
                onClick={() =>
                  setCurrentDesignElementIndex(
                    mappableElements.findIndex((e) => e === element)
                  )
                }
              >
                <DesignElementListItem>
                  <Space size="small">
                    <ValueOrFallback
                      value={elementTitle(element, groupIndex).title}
                      fallback={t("global.fallback.empty")}
                    />
                  </Space>
                </DesignElementListItem>
              </MenuItem>
            );
          })}
        </SubMenu>
      )}
    </Menu>
  );
};
